import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { AboutEmployeeAddDialogComponent } from 'src/app/dialogs/about-employee-add-dialog/about-employee-add-dialog.component';
import { AssessmentStatusEnum } from 'src/app/enums/assessment-status.enum';
import { EmployeeStatus } from 'src/app/enums/employee-status.enum';
import { GoalPeriodStatus } from 'src/app/enums/goal-period-status.enum';
import { GoalsHelper } from 'src/app/helpers/goals.helper';
import { ReviewPeriodHelper } from 'src/app/helpers/review-period.helper';
import { AboutEmployeeAddDialogData } from 'src/app/models/dialog/about-employee-add-dialog-data.model';
import { AboutEmployeeAddDialogResult } from 'src/app/models/dialog/about-employee-add-dialog-result.model';
import { Employee } from 'src/app/models/employee.model';
import { IResultModel, ResultModel } from 'src/app/models/general/result.model';
import { PagingRequest } from 'src/app/models/paging-request.model';
import { ReviewPeriodEmployeeForAdd } from 'src/app/models/review-period-employee-for-add.model';
import { Search } from 'src/app/models/search.model';
import { State } from 'src/app/models/state.model';
import { AssessmentService, EmployeeService, GoalService, StateService } from 'src/app/services';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { SearchConfig } from 'src/app/shared/models/search-config.model';
import { SearchOption } from 'src/app/shared/models/search-option.model';
import { ErrorMessageService } from 'src/app/shared/services';
import { AppState } from 'src/app/store/app.state';
import { SettingsState } from 'src/app/store/settings.state';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeHelper } from 'src/app/helpers/employee.helper';

@UntilDestroy()
@Component({
  selector: 'app-employee-review-period',
  templateUrl: './employee-review-period.component.html',
  styleUrls: ['./employee-review-period.component.scss']
})
export class EmployeeReviewPeriodComponent implements OnInit {
    
  private readonly itemsSubject = new BehaviorSubject<ListItem<Employee>[]>([]);
  public readonly items$ = this.itemsSubject.asObservable();

  public searchControl: UntypedFormControl;

  public minCharCount = 1;
  public EmployeeHelper = EmployeeHelper;

  // paging
  public total = 0;
  public pageSize = 20;
  public pageSizeOptions = [3, 5, 20, 50, 100];
  public page = 0;

  public state: State;
  
  public searchMessage = '';

  public employeeStatusList = EmployeeStatus;
  
  public get isValidCharacterCount(): boolean {
    return this.searchControl.value.length === 0 || this.searchControl.value.length >= this.minCharCount;
  }

  @ViewChild('paginator')
  paginator: MatPaginator;

  public get searchValue(): string {
    return this.searchControl.value;
  }

  public setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput) {
      this.pageSizeOptions = setPageSizeOptionsInput.split(',').map(str => +str);
    }
  }

  public searchEmployeeConfig: SearchConfig<Employee, number> = {
    mapItem: (model: Employee) => {
      return {
        id: model.id, 
        text: model.name,
        dropText: EmployeeHelper.getDisplayName(model),
        itemSource: model } as SearchOption<Employee, number>;
    },
    getList: this.employeeService.getList
  };
  
  public get isAura(): boolean {
    return this.settingState.reviewPeriod?.value?.isSpecialQuestionSetBasedAssessment;
  }

  public get isGoalPeriod(): boolean {
    return this.settingState.reviewPeriod?.value?.isGoalsEnabled; 
  }

  public get isPositionBased(): boolean {
    return this.settingState.reviewPeriod?.value?.isPositionBasedAssessment;
  }

  public get isEditable(): boolean {
    return ReviewPeriodHelper.isBlockEditable(this.settingState, this.state);
  }

  public get reviewPeriodId(): number {
    return this.settingState.reviewPeriod?.value?.id;
  }

  constructor(
    public settingState: SettingsState,
    public appState: AppState,
    private stateService: StateService,
    private employeeService: EmployeeService,
    private assessmentService: AssessmentService,
    private goalService: GoalService,
    private msgService: ErrorMessageService,
    private dialog: MatDialog,
    private router: Router,
    private translate: TranslateService
  ) {
    this.state = stateService.state;
  }

  public onIsGoalEvaluationTargetChange(employee: Employee, isGoalEvaluationTarget: boolean) {
    if (!employee.reviewPeriodEmployeeId) return;

    this.assessmentService.updateIsGoalEvaluationTarget(employee.reviewPeriodEmployeeId as number, isGoalEvaluationTarget)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          employee.isGoalEvaluationTarget = isGoalEvaluationTarget;
          this.msgService.showSnackBarMessage('saveSuccessful');
        },
        error: (err) => {
          this.msgService.handleError(err);
        }
      });
  }

  ngOnInit(): void {    
    this.searchControl = new UntypedFormControl('');

    combineLatest([this.settingState.reviewPeriod.obs$, this.settingState.reloadReviewPeriodEmployeeList.obs$])
    .pipe(untilDestroyed(this))
    .subscribe({next: (data) => {
      if (!data) return;

      this.loadList();
    }});
    
    this.searchControl.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.firstPage();
          this.setValidationMessage();
        }),
        switchMap(searchString => searchString.length === 0 || searchString.length >= this.minCharCount ? this.employeeSearch(searchString): of([])),
        untilDestroyed(this)
    ).subscribe({next: itemArray => {
      this.itemsSubject.next(itemArray);
    }});
  }

  public pagingSettingsChanged(data: PageEvent)
  {
    this.page = data.pageIndex;
    this.pageSize = data.pageSize;
    this.loadList();
  }

  public getGoalStatusText(status: GoalPeriodStatus): string {
      return this.goalService.getGoalStatusText(status);
  }

  private employeeSearch(searchString: string): Observable<ListItem<Employee>[]> {
    if (!this.settingState.reviewPeriod.value) return of([]);
    
    return this.employeeService.getList(this.buildPageRequest(searchString)).pipe(
      tap(data => {
        this.total = data.total;
        this.searchMessage = this.getResultMessage(data.list.length);
      }),
      map(result => result.list.map(x => new ListItem(x)) )
    );    
  }

  private buildPageRequest(searchString: string) {
    let data = {
      search: searchString,
      reviewPeriodId: this.settingState.reviewPeriod.value.id,
      pagingRequestInfo: {
        pageSize: this.pageSize,
        page: this.page + 1
      } as PagingRequest
    } as Search;

    return data;
  }

  public clearText() {
    this.searchControl.setValue('');
    //this.loadList();
  }

  private loadList() {
    this.employeeSearch(this.searchControl.value)
    .pipe(untilDestroyed(this))
    .subscribe({next: itemArray => {
      this.itemsSubject.next(itemArray);
    }});
  }

  public addEmployee(option: SearchOption<Employee, number>) {
    const rpId = this.settingState.reviewPeriod.value.id;
    let model = {
      reviewPeriodId: rpId,
      employeeId: option.itemSource.id
    } as ReviewPeriodEmployeeForAdd;

    if (this.isAura && this.isPositionBased)
    {
        const dialogData = {
          aboutEmployeeName : option.itemSource.name,
        } as AboutEmployeeAddDialogData;
    
        const dialogRef = this.dialog.open(AboutEmployeeAddDialogComponent, {
          minWidth: "500px",      
          data: dialogData
        });
    
        dialogRef.afterClosed().subscribe( (dialogResult: AboutEmployeeAddDialogResult) => {
          if (dialogResult)
          {         
            model.isAuraLeader = dialogResult.isAuraLeader;

            this.addToReviewPeriod(model);
          }
       });
    }
    else if (this.isAura)
    {
        model.isAuraLeader = true;
        this.addToReviewPeriod(model);
    }
    else
    {
        this.addToReviewPeriod(model);
    }
  }  

  public addToReviewPeriod(model: ReviewPeriodEmployeeForAdd)
  {
    this.employeeService.addToReviewPeriod(model)
    .pipe(untilDestroyed(this))
    .subscribe({
        next: () => {
          this.settingState.reloadReviewPeriodEmployeeList.value = true;
          this.msgService.showSnackBarMessage('addSuccessful');
        }, 
        error: (err: IResultModel<ResultModel>) => {
          this.msgService.handleError<ResultModel>(err);
        }
    });
  }

  public removeEmployee(option: SearchOption<Employee, number>) {
    const rpId = this.settingState.reviewPeriod.value.id;
    const model = {
      reviewPeriodId: rpId,
      employeeId: option.itemSource.id
    } as ReviewPeriodEmployeeForAdd;

    this.employeeService.deleteFromReviewPeriod(model)
    .pipe(untilDestroyed(this))
    .subscribe(
      { next: () => {
        this.settingState.reloadReviewPeriodEmployeeList.value = true;
        this.msgService.showSnackBarMessage('deleteSuccessful');
      }, error: (err: IResultModel<ResultModel>) => {
        this.msgService.handleError<ResultModel>(err);
      }
    });
  }
  
  public setValidationMessage() {
    if (!this.isValidCharacterCount)
      this.searchMessage = this.translate.instant('EMPLOYEE_REVIEW_PERIOD.MIN_CHAR_MESSAGE', { minCharCount: this.minCharCount });
  }

  public getResultMessage(count: number): string {
      if (this.total > count)
        return this.translate.instant('EMPLOYEE_REVIEW_PERIOD.RESULTS_MESSAGE', { total: this.total, count: count });
      else if (count === 0)
        return this.translate.instant('EMPLOYEE_REVIEW_PERIOD.NO_RESULTS');
      else
        return '';
  }

  public onGoalsClick(item: Employee) {
      this.router.navigate(['/goals', this.reviewPeriodId, item.id]);
  }

}
